import React, { useEffect } from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import "./DocumentSummaryModal.css";

interface DocumentSummaryModalProps {
  open: boolean;
  onClose: () => void;
  documentSummary: {
    filename?: string;
    title?: string;
    summary?: string;
    key_insights?: string[];
    common_problems?: string[];
    implemented_solutions?: string[];
    document_links?: { name: string; link: string }[];
  } | null;
  loading: boolean;
}

const DocumentSummaryModal: React.FC<DocumentSummaryModalProps> = ({
  open,
  onClose,
  documentSummary,
  loading,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const drawerWidth = isMobile ? '100%' : 500;

  useEffect(() => {
    if (open) {
      // Adjust main content when drawer is open
      const mainContent = document.querySelector('.ai-chat-main-container') as HTMLElement;
      const scrollContainer = document.querySelector('.scroll-ref-height') as HTMLElement;
      
      if (mainContent) {
        if (isMobile) {
          // On mobile, drawer takes full width, no adjustment needed
          mainContent.style.width = '100%';
        } else {
          // On desktop, adjust margin to accommodate drawer
          const drawerWidth = 500;
          mainContent.style.transition = 'margin-right 0.3s ease-in-out, width 0.3s ease-in-out';
          mainContent.style.marginRight = `${drawerWidth}px`;
          mainContent.style.width = `calc(100% - ${drawerWidth}px)`;
        }
      }

      if (scrollContainer && !isMobile) {
        scrollContainer.style.transition = 'margin-right 0.3s ease-in-out';
        scrollContainer.style.marginRight = '500px';
      }

      return () => {
        if (mainContent) {
          mainContent.style.marginRight = '0';
          mainContent.style.width = '';
        }
        if (scrollContainer) {
          scrollContainer.style.marginRight = '0';
        }
      };
    }
  }, [open, isMobile]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      className="document-summary-drawer"
      sx={{
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
          height: 'calc(100% - 65px)', 
          display: 'flex',
          top: "65px",
          flexDirection: 'column',
          overflowY: 'auto', 
        },
      }}
      SlideProps={{
        timeout: 300,
      }}
    >
      <Box 
        className="summary-drawer-content" 
        sx={{ 
          height: '100%', 
          display: 'flex', 
          flexDirection: 'column',
          overflow: 'auto',
        }}
      >
        <Box 
          className="summary-drawer-header"
          sx={{
            display: 'flex !important',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '20px 24px',
            borderBottom: '1px solid #e0e0e0',
            background: 'linear-gradient(135deg, #0088FF 0%, #0066CC 100%) !important',
            minHeight: '64px',
            height: 'auto',
            flexShrink: 0,
            position: 'relative',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 100,
            width: '100%',
            boxSizing: 'border-box',
            visibility: 'visible !important',
            opacity: '1 !important',
            transform: 'none !important',
          }}
        >
          <Tooltip 
            title={documentSummary?.filename || "Document Summary"} 
            placement="bottom"
            arrow
            enterDelay={300}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                flex: 1,
                overflow: 'hidden',
                marginRight: '16px',
                minWidth: 0,
              }}
            >
           
              <Typography 
                variant="h6" 
                className="summary-drawer-title"
                sx={{
                  color: 'white',
                  fontWeight: 600,
                  fontSize: '1.25rem',
                  flex: 1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  minWidth: 0,
                }}
              >
                {documentSummary?.filename || "Document Summary"}
              </Typography>
            </Box>
          </Tooltip>
          <IconButton
            onClick={onClose}
            className="summary-drawer-close"
            size="small"
            sx={{
              color: 'white',
              padding: '4px',
              flexShrink: 0,
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                transform: 'rotate(90deg)',
              },
              transition: 'transform 0.2s ease, background-color 0.2s ease',
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Box className="summary-drawer-body">
          {loading ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '300px',
                gap: 3,
                px: 3,
              }}
            >
              <CircularProgress size={48} thickness={3} sx={{ color: '#0088FF' }} />
              <Box sx={{ textAlign: 'center' }}>
                <Typography
                  variant="h6"
                  sx={{ color: '#0088FF', fontWeight: 700, mb: 0.5, fontSize: '1.1rem' }}
                >
                  Generating your summary...
                </Typography>
                <Typography variant="body2" sx={{ color: '#888', fontSize: '0.85rem' }}>
                  Analysing the document and extracting key insights for you.
                </Typography>
              </Box>
            </Box>
          ) : documentSummary?.summary ? (
            <>
              <Box className="summary-heading-container">
                <Typography
                  variant="h5"
                  className="summary-heading"
                  sx={{
                    color: '#0088FF',
                    fontWeight: 600,
                    fontSize: '1.1rem',
                    marginBottom: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  Summary
                </Typography>
              </Box>
              <Typography variant="body1" className="summary-text-content" sx={{ mb: 2 }}>
                {documentSummary.summary}
              </Typography>

              {documentSummary.key_insights && documentSummary.key_insights.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ color: '#0088FF', fontWeight: 600, mb: 0.5 }}>
                    Key Insights
                  </Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                    {documentSummary.key_insights.map((item, i) => (
                      <Typography key={i} component="li" variant="body2" sx={{ mb: 0.5, lineHeight: 1.6 }}>
                        {item}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              )}

              {documentSummary.common_problems && documentSummary.common_problems.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ color: '#0088FF', fontWeight: 600, mb: 0.5 }}>
                    Common Problems
                  </Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                    {documentSummary.common_problems.map((item, i) => (
                      <Typography key={i} component="li" variant="body2" sx={{ mb: 0.5, lineHeight: 1.6 }}>
                        {item}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              )}

              {documentSummary.implemented_solutions && documentSummary.implemented_solutions.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ color: '#0088FF', fontWeight: 600, mb: 0.5 }}>
                    Implemented Solutions
                  </Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                    {documentSummary.implemented_solutions.map((item, i) => (
                      <Typography key={i} component="li" variant="body2" sx={{ mb: 0.5, lineHeight: 1.6 }}>
                        {item}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              )}

              {documentSummary.document_links && documentSummary.document_links.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ color: '#0088FF', fontWeight: 600, mb: 0.5 }}>
                    Document Links
                  </Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                    {documentSummary.document_links.map((doc, i) => (
                      <Typography key={i} component="li" variant="body2" sx={{ mb: 0.5, lineHeight: 1.6 }}>
                        <a href={doc.link} target="_blank" rel="noreferrer">{doc.name}</a>
                      </Typography>
                    ))}
                  </Box>
                </Box>
              )}
            </>
          ) : (
            <Typography variant="body2" sx={{ color: "#666", fontStyle: "italic" }}>
              {"Summary not available for this document."}
            </Typography>
          )}
        </Box>
      </Box>
    </Drawer>
  );
};

export default DocumentSummaryModal;

